/**
 * Typed wrapper for the document-citation endpoint.
 *
 *   GET /api/document/<doc_id>
 *
 * NOTE: This endpoint lives in routes/api.py (legacy namespace),
 * not /api/platform/*. We consume it directly rather than wrap
 * it - the existing handler already has the right semantics
 * (login_required, citation-view gating, friendly-name overlay,
 * 403/404 errors). Re-exposing it under /api/platform/document
 * would just be duplication.
 *
 * Response shape: { success: true, document: { ... } } on 2xx,
 * or `{ error }` on 4xx/5xx (caught by the api() wrapper as
 * ApiError).
 */
import { api } from './client';

export interface DocumentDetail {
  id: string;
  title: string;
  /** Full extracted document text - can be very long. The panel
   *  renders this with `white-space: pre-wrap` and lets the user
   *  scroll inside the panel. */
  content: string;
  source_file: string;
  document_type: string;
  /** Top-level headings extracted from the doc, used for the
   *  "Contents" summary at the top of the panel. May be empty. */
  headings: string[];
}

interface DocumentEnvelope {
  success: true;
  document: DocumentDetail;
}

export async function getDocument(docId: string): Promise<DocumentDetail> {
  const env = await api<DocumentEnvelope>(
    `/api/document/${encodeURIComponent(docId)}`,
  );
  return env.document;
}
